import React from "react"
import { Modal, Typography } from "antd"

import CreateProjectForm from "./CreateProjectForm.component"

import { notify } from "shared/ui/Notifications/notify"

interface AddProjectModalProps {
  open: boolean
  onClose: () => void
}

/**
 * AddProjectModal component - Displays a modal for creating a new project.
 *
 * The caller opens it (the Projects header's New menu) and is told when it
 * closes: upon successfully creating a project or by other interactions
 * defined within the modal.
 * The project list refreshes via the create mutation's cache invalidation.
 *
 * @component
 * @returns  The AddProjectModal component.
 */
const AddProjectModal: React.FC<AddProjectModalProps> = ({ open, onClose }) => {
  const { Title } = Typography

  /** The form closes the modal through this; only closing is passed on. */
  const setIsModalVisible = (isVisible: boolean): void => {
    if (!isVisible) onClose()
  }

  /**
   * Handles actions after successfully creating a project.
   * It closes the modal and optionally triggers additional actions such as refreshing the project list.
   *
   * @function createProjectCallback
   * @param {string} projectId - The ID of the newly created project.
   */
  const createProjectCallback = (projectId: string): void => {
    if (projectId) {
      onClose()
      notify.success(
        "Project Created Successfully!",
        "The project has been created successfully."
      )
    }
  }

  return (
    <Modal
      open={open}
      centered={true}
      footer={false}
      closable={false}
      destroyOnHidden={true}
    >
      <Title style={{ marginTop: "0.5em" }} level={4}>
        Create Project
      </Title>
      <CreateProjectForm
        setIsModalVisible={setIsModalVisible}
        createProjectCallback={createProjectCallback}
      />
    </Modal>
  )
}

export default AddProjectModal
